/**
 * Platform detection for the install and notification onboarding.
 *
 * User-agent sniffing is normally a smell, but installing a PWA is genuinely
 * platform-specific: iOS has no install API at all and only Safari can add to
 * the home screen, while Android and desktop fire `beforeinstallprompt`. There
 * is no capability check that distinguishes "cannot install" from "can install,
 * just not programmatically", so the browser has to be identified.
 */
import { isIos, isStandalone, pushSupported } from './push'

export type Platform = 'ios' | 'android' | 'desktop'

/** How the user can install, which decides what the flow can offer. */
export type InstallMethod =
  /** Safari on iOS/iPadOS: manual Share → Add to Home Screen. */
  | 'ios-safari'
  /** Chrome/Firefox/Edge on iOS: cannot install; Safari is required. */
  | 'ios-other-browser'
  /** Android or desktop: `beforeinstallprompt` may offer a real button. */
  | 'prompt'
  /** Already installed, or the browser has no install path. */
  | 'none'

export const isAndroid = (): boolean =>
  typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent)

export function getPlatform(): Platform {
  if (isIos()) return 'ios'
  if (isAndroid()) return 'android'
  return 'desktop'
}

export const isMobile = (): boolean => isIos() || isAndroid()

/**
 * True for Safari proper.
 *
 * Every iOS browser is WebKit underneath and reports "Safari" in its UA, so the
 * other engines' own tokens (CriOS, FxiOS, EdgiOS, OPiOS) have to be excluded —
 * they matter because only real Safari can add to the home screen.
 */
export function isSafari(): boolean {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent
  if (/CriOS|FxiOS|EdgiOS|OPiOS|Chrome|Chromium|Android/i.test(ua)) return false
  return /Safari/i.test(ua)
}

export function getInstallMethod(): InstallMethod {
  if (isStandalone()) return 'none'
  if (isIos()) return isSafari() ? 'ios-safari' : 'ios-other-browser'
  return 'prompt'
}

/**
 * Whether notifications are reachable at all from where the user is standing.
 *
 * On iOS the Push API is hidden entirely until the app is installed, so
 * "unsupported" there means "not yet", not "never" — a distinction the copy has
 * to make or the user concludes the feature is broken.
 */
export function pushReachable(): boolean {
  if (pushSupported()) return true
  return isIos() && !isStandalone()
}

export interface PlatformInfo {
  platform: Platform
  installMethod: InstallMethod
  standalone: boolean
  mobile: boolean
  pushSupported: boolean
}

export function describePlatform(): PlatformInfo {
  return {
    platform: getPlatform(),
    installMethod: getInstallMethod(),
    standalone: isStandalone(),
    mobile: isMobile(),
    pushSupported: pushSupported(),
  }
}

export { isIos, isStandalone, pushSupported }
